import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../utils/Instance";
import { FaBell, FaCheck, FaCheckDouble, FaTrophy } from "react-icons/fa";

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const { profile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (profile?.accessToken) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [profile]);

  const fetchNotifications = async () => {
    const token = profile?.accessToken;
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE}accounts/notifications/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    const token = profile?.accessToken;
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE}accounts/notifications/unread-count/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    const token = profile?.accessToken;
    if (!token) return;

    try {
      const response = await fetch(
        `${API_BASE}accounts/notifications/${notificationId}/mark-read/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        // Update local state
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notificationId ? { ...notif, is_read: true } : notif
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    const token = profile?.accessToken;
    if (!token) return;

    try {
      const response = await fetch(
        `${API_BASE}accounts/notifications/mark-all-read/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        // Update all notifications to read
        setNotifications((prev) =>
          prev.map((notif) => ({ ...notif, is_read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Navigate to contest if available, with contributor highlight
    if (notification.contest_id) {
      // Check if contest has started before navigating
      try {
        const token = profile?.accessToken;
        if (token) {
          const response = await fetch(`${API_BASE}accounts/contests/${notification.contest_id}/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const contestData = await response.json();
            const now = new Date();
            const startTime = contestData.start_time ? new Date(contestData.start_time) : null;
            const hasStarted = startTime && now >= startTime;
            
            // Check if user should be redirected to waiting room
            const isUser = profile?.role === "user" || profile?.role === "Judge";
            const needsWaitingRoom = !hasStarted && isUser && startTime;
            
            if (needsWaitingRoom) {
              // Redirect to waiting room if contest hasn't started
              navigate(`/waiting-room/${notification.contest_id}`);
            } else {
              // Navigate to voting panel
              navigate(`/photo-voting/${notification.contest_id}`, {
                state: { 
                  highlightContributor: notification.contributor,
                  contributorName: notification.contributor_name
                }
              });
            }
          } else {
            // If fetch fails, try to navigate anyway (fallback)
            navigate(`/photo-voting/${notification.contest_id}`, {
              state: { 
                highlightContributor: notification.contributor,
                contributorName: notification.contributor_name
              }
            });
          }
        } else {
          navigate(`/photo-voting/${notification.contest_id}`, {
            state: { 
              highlightContributor: notification.contributor,
              contributorName: notification.contributor_name
            }
          });
        }
      } catch (error) {
        console.error("Error checking contest start time:", error);
        // Fallback: navigate to voting panel
        navigate(`/photo-voting/${notification.contest_id}`, {
          state: { 
            highlightContributor: notification.contributor,
            contributorName: notification.contributor_name
          }
        });
      }
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "favorite_contest_join":
        return <FaTrophy className="text-[#ceb46a]" size={20} />;
      case "contest_start":
        return <FaBell className="text-blue-500" size={20} />;
      case "contest_end":
        return <FaCheck className="text-green-500" size={20} />;
      default:
        return <FaBell className="text-gray-400" size={20} />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="Notifications" />

        {/* Header with Mark All Read */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#ceb46a]">
              Notifications
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-2 px-4 py-2 bg-[#ceb46a] text-black rounded-lg hover:bg-[#b89d54] transition"
            >
              <FaCheckDouble />
              Mark All Read
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-[#ceb46a] text-xl">Loading notifications...</div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <FaBell className="mx-auto text-gray-600 mb-4" size={60} />
            <p className="text-gray-400 text-xl mb-2">No notifications yet</p>
            <p className="text-gray-500">
              You'll receive notifications when favorited contributors join contests
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${
                  notification.is_read
                    ? "bg-[#1c1c1c] hover:bg-[#252525]"
                    : "bg-[#2a2a2a] hover:bg-[#333333] border-l-4 border-[#ceb46a]"
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.notification_type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3
                        className={`text-base md:text-lg font-semibold ${
                          notification.is_read ? "text-gray-300" : "text-white"
                        }`}
                      >
                        {notification.title}
                      </h3>
                      {!notification.is_read && (
                        <span className="flex-shrink-0 w-2 h-2 bg-[#ceb46a] rounded-full mt-2"></span>
                      )}
                    </div>
                    <p
                      className={`text-sm mt-1 ${
                        notification.is_read ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{formatTime(notification.created_at)}</span>
                      {notification.contest_title && (
                        <span className="text-[#ceb46a]">
                          📍 {notification.contest_title}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
